import React, { Fragment, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Badge, Button, Card, Col, Row, Spinner, Table } from "react-bootstrap";
import PageTitle from "../../layouts/PageTitle";
import api, { getFileUrl } from "../../../api";
import swal from "sweetalert";
import SignedBillActions from "../Common/SignedBillActions";

const money = (value) =>
 new Intl.NumberFormat("en-IN", {
 style: "currency",
 currency: "INR",
 }).format(Number(value || 0));

const statusVariant = (status) => {
 if (status === "Paid") return "success";
 if (status === "Partially Paid") return "warning";
 if (status === "Rejected") return "danger";
 return "info";
};

const ContractorBillingView = () => {
 const { id } = useParams();
 const navigate = useNavigate();
 const [bill, setBill] = useState(null);
 const [loading, setLoading] = useState(true);

 useEffect(() => {
 const loadBill = async () => {
 try {
 setLoading(true);
 const res = await api.get(`/contractor-billings/${id}`);
 if (res.data.success) setBill(res.data.data);
 } catch (error) {
 swal("Error", "Failed to load contractor bill", "error");
 } finally {
 setLoading(false);
 }
 };

 loadBill();
 }, [id]);

 const remaining = Math.max(
 Number(bill?.totalAmount || 0) - Number(bill?.paidAmount || 0),
 0
 );

 if (loading) {
 return <div className="text-center py-5"><Spinner /></div>;
 }

 if (!bill) {
 return (
 <Fragment>
 <PageTitle activeMenu="Contractor Bill View" motherMenu="Procurement" />
 <Card className="shadow-sm border-0">
 <Card.Body className="text-center text-muted py-5">
 Contractor bill not found.
 </Card.Body>
 </Card>
 </Fragment>
 );
 }

 return (
 <Fragment>
 <PageTitle activeMenu="Contractor Bill View" motherMenu="Procurement" />
 <Card className="shadow-sm border-0">
 <Card.Header className="bg-white d-flex justify-content-between align-items-center">
 <Card.Title className="mb-0">
 {bill.billNo}
 <Badge bg={statusVariant(bill.status)} className="ms-2">{bill.status}</Badge>
 </Card.Title>
 <Button variant="light" onClick={() => navigate(-1)}>
 <i className="fa fa-arrow-left me-2"></i>Back
 </Button>
 </Card.Header>
 <Card.Body>
 <Row className="g-3 mb-4">
 <Col md={4}>
 <div className="border rounded p-3 h-100">
 <div className="text-muted small">CWO</div>
 <div className="fw-bold">{bill.contractualWorkOrder?.cwoNo || "-"}</div>
 </div>
 </Col>
 <Col md={4}>
 <div className="border rounded p-3 h-100">
 <div className="text-muted small">Work Order</div>
 <div className="fw-bold">{bill.workOrder?.woNo || "-"}</div>
 </div>
 </Col>
 <Col md={4}>
 <div className="border rounded p-3 h-100">
 <div className="text-muted small">Vendor</div>
 <div className="fw-bold">{bill.vendor?.name || "-"}</div>
 </div>
 </Col>
 <Col md={3}>
 <div className="border rounded p-3 h-100">
 <div className="text-muted small">Invoice No</div>
 <div className="fw-bold">{bill.invoiceNo || "-"}</div>
 </div>
 </Col>
 <Col md={3}>
 <div className="border rounded p-3 h-100">
 <div className="text-muted small">Bill Date</div>
 <div className="fw-bold">
 {bill.invoiceDate ? new Date(bill.invoiceDate).toLocaleDateString("en-IN") : "-"}
 </div>
 </div>
 </Col>
 <Col md={3}>
 <div className="border rounded p-3 h-100">
 <div className="text-muted small">Paid</div>
 <div className="fw-bold text-success">{money(bill.paidAmount)}</div>
 </div>
 </Col>
 <Col md={3}>
 <div className="border rounded p-3 h-100">
 <div className="text-muted small">Remaining</div>
 <div className="fw-bold text-danger">{money(remaining)}</div>
 </div>
 </Col>
 </Row>

 {bill.attachment?.fileUrl && (
 <div className="mb-4">
 <a
 href={getFileUrl(bill.attachment.fileUrl)}
 target="_blank"
 rel="noreferrer"
 className="btn btn-outline-primary btn-sm"
 >
 <i className="fa fa-paperclip me-2"></i>
 {bill.attachment.fileName || "View Uploaded Bill"}
 </a>
 </div>
 )}

 <div className="mb-4 border rounded p-3">
 <div className="d-flex justify-content-between align-items-start flex-wrap gap-2">
 <div>
 <div className="text-muted small">Signed Bill</div>
 <div className="fw-semibold">
 {bill.signedCopy?.uploadedAt
 ? new Date(bill.signedCopy.uploadedAt).toLocaleDateString("en-IN")
 : "Not uploaded"}
 </div>
 </div>
 <SignedBillActions
 id={bill._id}
 type="contractor-billings"
 signedCopy={bill.signedCopy}
 onUploadSuccess={async () => {
 const res = await api.get(`/contractor-billings/${id}`);
 if (res.data.success) setBill(res.data.data);
 }}
 />
 </div>
 </div>

 <div className="table-responsive">
 <Table bordered hover className="align-middle">
 <thead className="table-light">
 <tr>
 <th>#</th>
 <th>WLINO</th>
 <th>Item</th>
 <th>Qty</th>
 <th>Rate</th>
 <th>Stage</th>
 <th>Stage %</th>
 <th>Amount</th>
 </tr>
 </thead>
 <tbody>
 {(bill.items || []).map((item, index) => (
 <tr key={`${item.boqItemId}-${index}`}>
 <td>{index + 1}</td>
 <td>{item.wlino || "-"}</td>
 <td>
 <strong>{item.itemName}</strong>
 <div className="small text-muted">{item.description}</div>
 </td>
 <td>{item.quantity} {item.unit}</td>
 <td>{money(item.price)}</td>
 <td>
 {(item.stages || []).length > 0 ? (
 <div className="d-flex flex-wrap gap-1">
 {item.stages.map((stage, idx) => (
 <Badge key={idx} bg="info" className="small">{stage}</Badge>
 ))}
 </div>
 ) : (
 <span className="text-muted">-</span>
 )}
 </td>
 <td>{item.stagePercentage > 0 ? `${item.stagePercentage}%` : "-"}</td>
 <td>{money(item.amount)}</td>
 </tr>
 ))}
 </tbody>
 </Table>
 </div>

 <Row className="justify-content-end mt-3">
 <Col md={4}>
 <div className="border rounded p-3 bg-light">
 <div className="d-flex justify-content-between">
 <span>Sub Total</span>
 <strong>{money(bill.subTotal)}</strong>
 </div>
 {bill.cgst > 0 && (
 <div className="d-flex justify-content-between">
 <span>CGST ({bill.cgst}%)</span>
 <strong>{money(bill.cgstAmount)}</strong>
 </div>
 )}
 {bill.sgst > 0 && (
 <div className="d-flex justify-content-between">
 <span>SGST ({bill.sgst}%)</span>
 <strong>{money(bill.sgstAmount)}</strong>
 </div>
 )}
 {bill.igst > 0 && (
 <div className="d-flex justify-content-between">
 <span>IGST ({bill.igst}%)</span>
 <strong>{money(bill.igstAmount)}</strong>
 </div>
 )}
 <hr />
 <div className="d-flex justify-content-between text-primary">
 <span>Total</span>
 <strong>{money(bill.totalAmount)}</strong>
 </div>
 </div>
 </Col>
 </Row>
 </Card.Body>
 </Card>
 </Fragment>
 );
};

export default ContractorBillingView;
